import { enSidebar } from "./enSidebar";

export const enFeatureUsage = {
	title: "Feature usage",
	tabs: {
		adoption: "Adoption",
		unused: "Unused features",
		company: "Company view",
		all: "All customers",
	},
	period: "Period",
	days: "{{count}} days",
	year: "Last 12 months",
	plan: "Plan",
	allPlans: "All paid plans",
	trial: "Include trial and demo companies",
	internal: "Include company 1 and 2225",
	company: "Company",
	pickCompany: "Select a company",
	noCompanies: "No companies with retained usage.",
	loading: "Loading usage…",
	error: "Usage could not be loaded. Please try again.",
	retry: "Try again",
	empty: "No usage recorded yet.",
	capture: "Capture starts when product analytics is enabled.",
	rollup: "Daily usage appears after the first completed rollup.",
	noMatches: "No features match this selection.",
	notMeasured: "Not measured",
	noHistory: "No history",
	new: "New",
	dash: "—",
	window: "{{range}}, complete UTC days",
	rolledUp: "Rolled up through {{date}} UTC",
	staffExcluded: "Staff excluded",
	coverage: "Distinct users measured on {{count}} UTC days",
	companies: "{{used}} / {{total}} companies",
	catalog: "{{count}} catalog features",
	breadth: "{{used}} of {{total}}",
	on: "Analytics on",
	off: "Analytics off",
	unusedHeading: "Low use and enabled features without recorded use",
	allHeading: "All customers · retained history",
	allNote:
		"Includes deleted accounts’ history. No company, plan or trial/demo filter applies. Staff events are always excluded at capture.",
	distinctNote:
		"Distinct users are deduplicated per company, feature and UTC day across all actions and surfaces. Unmeasured days are gaps, never zero. Counts cannot be added across features or days.",
	historyNote:
		"Current entitlement state, not historical entitlement. No history means no recorded use within retained history, not lifetime non-use.",
	sample: "{{count}} enabled companies with no recorded use; sample (at most 10): {{names}}",
	reasons: {
		zero: "Zero in period",
		"near-zero": "Near-zero",
		"flag-on-never-used": "Flag on, no recorded use",
	},
	previous: "Previous",
	next: "Next",
	page: "Page {{page}} of {{pages}}",
	sort: "Sort by {{column}}",
	surfaces: { web: "Web", extension: "Extension", mcp: "MCP", api: "API" },
	metrics: {
		feature: "Feature",
		adoption: "Company adoption",
		events: "Events",
		average: "Avg daily distinct users",
		trend: "Trend",
		surfaces: "Surface split",
		flagOn: "Flag-on companies",
		companiesUsed: "Companies that used it",
		lastEvent: "Last event date",
		reason: "Reason",
		sample: "Enabled, no recorded use",
		dailyEvents: "Daily events",
		dailyUsers: "Daily distinct users",
		breadth: "Feature breadth",
		lastActivity: "Last activity",
		analytics: "Analytics on/off for this company",
		allEvents: "Events in period",
		surfaceEvents: "Surface events",
		cohort: "Eligible companies",
		coverage: "Measured days",
		window: "UTC window",
		catalog: "Catalog size",
		pages: "Feature pages",
		period: "Period",
		trial: "Trial and demo cohort",
		internal: "Internal companies",
	},
	help: {
		feature:
			"Catalog label and stable feature key; grouped by catalog area. Sort alphabetically within each area.",
		adoption:
			"Eligible companies with at least one event for this feature divided by the cohort’s companies. All actions and surfaces count; the cohort sentence states the current eligibility rules.",
		events:
			"Sum of recorded events for this feature in the selected complete UTC days and eligible cohort, across all actions and surfaces. One event is one captured action, not a person or a business outcome.",
		average:
			"Mean of per-company, per-feature, per-day distinct users, deduplicated across all actions and surfaces. Divide by eligible companies × measured UTC days (distinctUsersDates), including zero company-days. Rounded to one decimal. Not measured means no measured days; never a period-distinct count.",
		trend:
			"Event change: (current − previous) / previous × 100, compared with the preceding equal UTC window for the same current cohort, all actions and surfaces. New if previous is zero and current is positive; — if both are zero.",
		surfaces:
			"Share of this feature’s period events by Web, Extension, MCP and API, across all actions. Shares sum to 100% when events exist (rounding may differ); all are zero with no events. API includes server-initiated work. Sort by Web share.",
		flagOn:
			"Current eligible companies whose linked catalog flag resolves to enabled, including global defaults and company overrides. — means the catalog has no flag link.",
		companiesUsed:
			"Eligible companies with at least one event for this feature in the selected period, all actions and surfaces. This is a company count, not a user count.",
		lastEvent:
			"Latest UTC daily date with events for this feature among current eligible companies across retained history. No history means no retained daily row, not lifetime non-use.",
		reason:
			"Zero = no events in the selected period; near-zero = positive events and at most {{threshold}} of the cohort’s companies used the feature. Flag on, no recorded use = at least one currently enabled company with no retained usage of this feature. Several reasons can apply.",
		sample:
			"Number of currently enabled cohort companies with no recorded use in retained history, followed by at most 10 example company names. This is not proof of lifetime non-use.",
		dailyEvents:
			"For this company and feature, recorded events per complete UTC day across all actions and surfaces. Zero means no recorded events. Darker cells mean more events; focus or hover a cell for the exact value. This is not a user count.",
		dailyUsers:
			"Distinct users deduplicated per company, feature and UTC day across all actions and surfaces. Each value belongs to one feature and day. Not measured is a gap, never zero; do not sum across features or days.",
		breadth:
			"Catalog features with at least one event for this company in the selected complete UTC days, across all actions and surfaces, out of the full current catalog size.",
		lastActivity:
			"Latest complete UTC daily date with a product usage event for this company across retained history. Daily precision only; staff excluded.",
		analytics:
			"Read-only company analytics state. On means capture is enabled for this company. The company-wide switch is managed separately.",
		allEvents:
			"Recorded events for this feature across all customers and selected complete UTC days, all actions and surfaces. Includes deleted accounts’ retained history; staff excluded. No company, plan or trial filter applies.",
		allDaily:
			"Daily recorded events for this feature across all customers, actions and surfaces. Includes deleted accounts’ retained history. Each value represents one complete UTC day; zero means no recorded events.",
		surfaceEvents:
			"Recorded events for this feature by Web, Extension, MCP and API across all customers in the selected period, including deleted accounts. API includes server-initiated work.",
		cohort:
			"Current eligible companies with analytics on and at least one product event in the selected period. The returned sentence specifies paying or paying plus trial/demo companies; internal-company inclusion follows the toggle. Staff events are always excluded at capture.",
		coverage:
			"Only the returned distinctUsersDates were measured. The mean uses these days, including zero company-days; missing dates are not measured. Measured UTC dates: {{dates}}.",
		window:
			"All figures cover complete UTC days through the last completed rollup. The current partial UTC day is excluded.",
		catalog:
			"Number of features in the full current product feature catalog, including features with no usage.",
		pages:
			"Ten feature rows per page, grouped by catalog area. Hidden rows do not render timelines.",
		period:
			"Number of complete UTC days ending on the last rolled-up date. All customers also offers 365 days.",
		trial:
			"Widens the eligible cohort to include trial and demo companies with analytics on and at least one event in the selected period. The cohort sentence names the selected group.",
		internal:
			"Include companies 1 and 2225 in the eligible cohort. Staff events are always excluded at capture and cannot be restored by this toggle.",
	},
};

// Register the staff page and its navigation together without growing the oversized English registry.
export const enFeatureUsageTranslations = { sidebar: enSidebar, featureUsage: enFeatureUsage };
